/**
 * Foreground loading sessions with truthful work-unit progress.
 * Progress is completedWeight / totalWeight. Time does not move the bar.
 */
export type ForegroundLoadingSnapshot = {
  activeCount: number;
  visible: boolean;
  progress: number;
  generation: number;
  activeSinceMs: number | null;
  activeOperation: string | null;
};

export type ForegroundWorkKind = "navigation" | "lazy_route" | "query" | "task" | "commit";

export type ForegroundLoadingController = {
  begin: (label?: string) => number;
  /** Increment the ref-count and show the bar in the same turn (no show delay). */
  beginImmediate: (label?: string) => number;
  beginWork: (kind: ForegroundWorkKind, label?: string) => number;
  beginSession: () => number;
  end: (token: number) => void;
  resetForNavigation: () => void;
  /** Drop every token and hide immediately. Tests only. */
  resetIdle: () => void;
  getSnapshot: () => ForegroundLoadingSnapshot;
  subscribe: (listener: () => void) => () => void;
  setNow: (fn: () => number) => void;
  tick: () => void;
};

export const SHOW_DELAY_MS = 175;
/** Presentation-only hold at 100%. Does not affect completion authority. */
export const HIDE_DELAY_MS = 200;

type WorkUnit = {
  token: number;
  kind: ForegroundWorkKind;
  label: string;
  done: boolean;
};

export function createForegroundLoadingController(options?: {
  showDelayMs?: number;
  hideDelayMs?: number;
  now?: () => number;
}): ForegroundLoadingController {
  const showDelayMs = options?.showDelayMs ?? SHOW_DELAY_MS;
  const hideDelayMs = options?.hideDelayMs ?? HIDE_DELAY_MS;
  let nowFn = options?.now ?? (() => Date.now());
  let manualClock = options?.now != null;

  let generation = 0;
  let nextToken = 1;
  const units = new Map<number, WorkUnit>();
  let visible = false;
  let progress = 0;
  let showAt: number | null = null;
  let hideAt: number | null = null;
  let activeSinceMs: number | null = null;
  const listeners = new Set<() => void>();

  let snapshot: ForegroundLoadingSnapshot = {
    activeCount: 0,
    visible: false,
    progress: 0,
    generation: 0,
    activeSinceMs: null,
    activeOperation: null,
  };
  let showTimer: ReturnType<typeof setTimeout> | null = null;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const roundedProgress = (value: number) => Math.round(Math.max(0, Math.min(100, value)) * 100) / 100;

  const incompleteUnits = () => [...units.values()].filter((unit) => !unit.done);
  const activeCount = () => incompleteUnits().length;

  const activeOperation = (): string | null => {
    const open = incompleteUnits().find((unit) => unit.kind !== "commit") ?? incompleteUnits()[0];
    return open?.label ?? null;
  };

  const emit = () => {
    snapshot = {
      activeCount: activeCount(),
      visible,
      progress: roundedProgress(progress),
      generation,
      activeSinceMs,
      activeOperation: activeOperation(),
    };
    listeners.forEach((listener) => listener());
  };

  const clearTimers = () => {
    if (showTimer != null) clearTimeout(showTimer);
    if (hideTimer != null) clearTimeout(hideTimer);
    showTimer = null;
    hideTimer = null;
  };

  const scheduleTimers = () => {
    clearTimers();
    if (manualClock) return;
    const t = nowFn();
    if (activeCount() > 0 && !visible && showAt != null) {
      showTimer = setTimeout(() => tick(), Math.max(0, showAt - t));
    } else if (activeCount() === 0 && visible && hideAt != null) {
      hideTimer = setTimeout(() => tick(), Math.max(0, hideAt - t));
    }
  };

  const rawProgress = (): number => {
    if (units.size === 0) return 0;
    const completed = [...units.values()].filter((unit) => unit.done).length;
    return (completed / units.size) * 100;
  };

  const maybeCompleteCommit = () => {
    const commit = [...units.values()].find((unit) => unit.kind === "commit" && !unit.done);
    if (!commit) return;
    const others = [...units.values()].filter((unit) => unit.kind !== "commit");
    if (others.length === 0) return;
    if (others.every((unit) => unit.done)) commit.done = true;
  };

  const ensureCommitUnit = () => {
    const existing = [...units.values()].find((unit) => unit.kind === "commit");
    if (existing) {
      if (existing.done) existing.done = false;
      return;
    }
    const token = nextToken++;
    units.set(token, { token, kind: "commit", label: "Destination ready", done: false });
  };

  const applyTruthfulProgress = () => {
    maybeCompleteCommit();
    const raw = rawProgress();
    const remaining = activeCount() > 0;
    if (!remaining && units.size > 0) {
      progress = 100;
      return;
    }
    const next = Math.min(raw, 99.99);
    progress = Math.max(progress, next);
    if (remaining && progress >= 100) progress = 99.99;
  };

  const runStep = (forceEmit = false) => {
    const t = nowFn();
    let changed = false;
    if (units.size === 0) {
      if (visible || progress !== 0 || activeSinceMs != null) {
        visible = false;
        progress = 0;
        hideAt = null;
        showAt = null;
        activeSinceMs = null;
        changed = true;
      }
      if (changed || forceEmit) emit();
      scheduleTimers();
      return;
    }
    const before = progress;
    applyTruthfulProgress();
    if (progress !== before) changed = true;
    if (activeCount() > 0) {
      if (activeSinceMs == null) activeSinceMs = t;
      hideAt = null;
      if (!visible && showAt == null) {
        showAt = t + showDelayMs;
      }
      if (!visible && showAt != null && t >= showAt) {
        visible = true;
        showAt = null;
        changed = true;
      }
    } else {
      showAt = null;
      if (visible) {
        if (progress !== 100) {
          progress = 100;
          changed = true;
        }
        if (hideAt == null) hideAt = t + hideDelayMs;
        if (hideAt != null && t >= hideAt) {
          visible = false;
          progress = 0;
          hideAt = null;
          activeSinceMs = null;
          units.clear();
          changed = true;
        }
      } else {
        if (progress !== 0) {
          progress = 0;
          changed = true;
        }
        hideAt = null;
        if (activeSinceMs != null) {
          activeSinceMs = null;
          changed = true;
        }
        if (units.size > 0 && [...units.values()].every((unit) => unit.done)) {
          units.clear();
        }
      }
    }
    if (changed || forceEmit) emit();
    scheduleTimers();
  };

  const tick = () => runStep(false);

  const addUnit = (kind: ForegroundWorkKind, label: string, immediate: boolean): number => {
    ensureCommitUnit();
    const token = nextToken++;
    units.set(token, { token, kind, label: label.trim() || kind, done: false });
    if (activeSinceMs == null) activeSinceMs = nowFn();
    if (immediate) {
      showAt = null;
      hideAt = null;
      visible = true;
    }
    runStep(true);
    return token;
  };

  return {
    begin(label) {
      return addUnit("task", label ?? "Loading", false);
    },
    beginImmediate(label) {
      return addUnit("task", label ?? "Loading", true);
    },
    beginWork(kind, label) {
      return addUnit(kind, label ?? kind, true);
    },
    beginSession() {
      generation += 1;
      units.clear();
      visible = false;
      progress = 0;
      showAt = null;
      hideAt = null;
      activeSinceMs = null;
      clearTimers();
      runStep(true);
      return generation;
    },
    end(token) {
      const unit = units.get(token);
      if (!unit || unit.done) return;
      unit.done = true;
      runStep(true);
    },
    resetForNavigation() {
      generation += 1;
      units.clear();
      visible = false;
      progress = 0;
      showAt = null;
      hideAt = null;
      activeSinceMs = null;
      clearTimers();
      runStep(true);
    },
    resetIdle() {
      generation += 1;
      units.clear();
      visible = false;
      progress = 0;
      showAt = null;
      hideAt = null;
      activeSinceMs = null;
      clearTimers();
      runStep(true);
    },
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setNow(fn) {
      nowFn = fn;
      manualClock = true;
      clearTimers();
    },
    tick,
  };
}
